import {inject, Injectable} from '@angular/core';
import {MatSnackBar} from "@angular/material/snack-bar";
import {HttpErrorResponse} from "@angular/common/http";
import {TranslateService} from "@ngx-translate/core";
import {httpErrorKey} from "@core/utils/http-error.utils";

@Injectable({
  providedIn: 'root'
})
export class SnackbarService {

  /**
   * Duración estándar (ms) de todos los snackbars de la app.
   * Fuente única de verdad: los llamadores no deben pasar duraciones
   * literales, sino apoyarse en este valor por defecto de `open()`.
   */
  longDuration: number = 4000;

  private horizontalPosition: string = 'center';
  private verticalPosition: string = 'bottom';

  private readonly translate = inject(TranslateService);

  constructor(private _snackBar: MatSnackBar) {
  }

  /**
   * Muestra un snackbar de error a partir de una clave i18n (o de un texto ya resuelto).
   * @param messageKey Clave i18n (ej. `errors.http.403`) o texto literal.
   * @param params Parámetros de interpolación opcionales para la traducción.
   */
  error(messageKey: string, params?: Record<string, unknown>) {
    const text = this.translate.instant(messageKey, params);
    this.open(text, this.longDuration, this.translate.instant('buttons.close'));
  }

  /**
   * Muestra el snackbar de error apropiado para un `HttpErrorResponse`.
   *
   * Centraliza el mapeo status → clave i18n (`httpErrorKey`), el logging técnico
   * y la regla de negocio del 401: un 401 suele ser expiración de sesión, ya
   * gestionada por el flujo `session-expired`, por lo que NO se muestra snackbar
   * para evitar doble feedback.
   *
   * @param error Error capturado en un handler `error:` de una suscripción.
   */
  notifyHttpError(error: unknown) {
    const status = error instanceof HttpErrorResponse ? error.status : undefined;
    console.error('[HTTP error]', status, error);

    // El 401 se delega al flujo de sesión expirada (sin doble feedback).
    if (status === 401) {
      return;
    }

    this.error(httpErrorKey(error));
  }

  /**
   * Main function to show a Material Snackbar.
   * @param message
   * @param action
   * @param duration
   * @param actionCallback
   */
  open(message: string, duration: number | null = this.longDuration, action?: string, actionCallback?: () => void) {
    if (duration) {
      this.openSnackbarWithDuration(message, duration, action, actionCallback);
    } else {
      this.openSnackbarWhitoutDuration(message, action, actionCallback);
    }
  }

  /**
   * Provides a Snackbar with duration.
   * @param message
   * @param duration
   * @param action
   * @param actionCallback
   * @private
   */
  private openSnackbarWithDuration(message: string, duration: number, action?: string, actionCallback?: () => void) {
    this._snackBar.open(message, action, {
      duration: duration,
      // @ts-ignore
      horizontalPosition: this.horizontalPosition,
      // @ts-ignore
      verticalPosition: this.verticalPosition,
      panelClass: ['custom-snackbar', 'snackbar-safe-bottom']
    }).onAction().subscribe(_action => {
      if (actionCallback) {
        actionCallback();
      }
    });
  }

  /**
   * Provides a Snackbar without duration.
   * @param message
   * @param action
   * @param actionCallback
   * @private
   */
  private openSnackbarWhitoutDuration(message: string, action?: string, actionCallback?: () => void) {
    this._snackBar.open(message, action, {
      // @ts-ignore
      horizontalPosition: this.horizontalPosition,
      // @ts-ignore
      verticalPosition: this.verticalPosition,
      panelClass: ['custom-snackbar', 'snackbar-safe-bottom']
    }).onAction().subscribe(_action => {
      if (actionCallback) {
        actionCallback();
      }
    });
  }

}
